import { useState } from "react";
import {
  Image,
  Linking,
  Platform,
  ScrollView,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import * as Clipboard from "expo-clipboard";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Check,
  ClipboardCopy,
  ExternalLink,
  Info,
  WalletCards,
} from "lucide-react-native";
import { useRegion, useTranslations } from "@src/packages/i18n";
import {
  FINANCING_PROVIDERS,
  isFinancialClaimCurrent,
  type FinancialClaim,
  type FinancialClaimKind,
  type FinancingProvider,
} from "@src/packages/costs/financialContent";
import { EMBER } from "../../../onboarding/theme";
import type { CostsStackParamList } from "../../types";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { BackLink, CardSurface, IconButton, haptic } from "@louise/moon-ui";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { useSmartBack } from "../../hooks/useSmartBack";
import ScreenHeader from "../../components/ScreenHeader";
import ApplicantInfoDialog, {
  formatSnapshotForClipboard,
  type ApplicantSnapshot,
} from "./ApplicantInfoDialog";
import FinancingFactSheet from "./FinancingFactSheet";
import { useApplicationPrefill } from "./useApplicationPrefill";
import { semanticColors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<CostsStackParamList, "Financing">;

const PROVIDER_LOGOS: Record<string, ImageSourcePropType | null> = {
  capexmd:
    require("../../../assets/images/financing/capexmd.png") as ImageSourcePropType,
  patientfi:
    require("../../../assets/images/insurance/patientfi.png") as ImageSourcePropType,
  gaia: require("../../../assets/images/financing/gaia.png") as ImageSourcePropType,
  "future-family":
    require("../../../assets/images/insurance/futurefamily.png") as ImageSourcePropType,
  prosper:
    require("../../../assets/images/insurance/prosper.png") as ImageSourcePropType,
};

function getClaim(
  provider: FinancingProvider,
  kinds: FinancialClaimKind[],
): FinancialClaim | undefined {
  return provider.claims.find(
    (claim) => kinds.includes(claim.kind) && isFinancialClaimCurrent(claim),
  );
}

function ProviderLogo({
  provider,
  size = 48,
}: {
  provider: FinancingProvider;
  size?: number;
}) {
  const logo = PROVIDER_LOGOS[provider.id];
  if (logo) {
    const logoWidth =
      provider.id === "capexmd" ? 112 : provider.id === "gaia" ? 78 : size;
    const logoHeight =
      provider.id === "capexmd" ? 42 : provider.id === "gaia" ? 42 : size;

    return (
      <View
        style={{
          width: logoWidth,
          height: size,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Image
          source={logo}
          resizeMode="contain"
          style={{ width: logoWidth, height: logoHeight }}
        />
      </View>
    );
  }

  return (
    <Text
      className="font-dm-sans"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.52,
        lineHeight: size,
        fontWeight: "800",
        color: EMBER.colors.textMid,
        textAlign: "center",
      }}
    >
      {provider.name.slice(0, 1)}
    </Text>
  );
}

function ComparisonCell({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flex: 1, minWidth: 138 }}>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 11, lineHeight: 15, color: EMBER.colors.textLight }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        numberOfLines={4}
        style={{
          marginTop: 4,
          fontSize: 13,
          lineHeight: 18,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function ProviderCard({
  provider,
  onOpenDetails,
}: {
  provider: FinancingProvider;
  onOpenDetails: () => void;
}) {
  const t = useTranslations();
  const pricingClaim = getClaim(provider, ["promotion", "apr"]);
  const fundingClaim = getClaim(provider, ["loan-maximum"]);
  const creditClaim = getClaim(provider, ["credit-check"]);
  const accessClaim = getClaim(provider, ["availability"]);
  const currentClaims = provider.claims.filter((claim) =>
    isFinancialClaimCurrent(claim),
  );
  const staleClaimCount = provider.claims.length - currentClaims.length;
  const pricingValue =
    pricingClaim?.value ?? t("costs.financing_page.verify_with_provider");
  const fundingValue =
    fundingClaim?.value ??
    (provider.productType === "fertility-insurance"
      ? provider.productLabel
      : t("costs.financing_page.verify_with_provider"));
  const creditValue =
    creditClaim?.value ??
    (provider.productType === "fertility-insurance"
      ? t("costs.financing_page.not_a_credit_product")
      : t("costs.financing_page.verify_with_provider"));
  const accessValue =
    accessClaim?.value ?? t("costs.financing_page.verify_with_provider");

  return (
    <CardSurface style={{ padding: 18, gap: 14 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
        <ProviderLogo provider={provider} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              lineHeight: 23,
              fontWeight: "800",
              color: EMBER.colors.text,
            }}
          >
            {provider.name}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 3,
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.textMid,
            }}
          >
            {provider.productLabel}
          </Text>
        </View>
        <View style={{ flexDirection: "row", gap: 6 }}>
          <IconButton
            onPress={onOpenDetails}
            accessibilityLabel={t("costs.financing_page.provider_details")}
            hitSlop={8}
            size={32}
            surface="soft"
            backgroundColor={semanticColors.surface.subtle}
            pressedBackgroundColor={semanticColors.overlay.pressed}
            pressedOpacity={1}
          >
            <Info size={16} color={EMBER.colors.textMid} strokeWidth={1.8} />
          </IconButton>
          <IconButton
            onPress={() => Linking.openURL(provider.url)}
            accessibilityLabel={t("costs.financing_page.visit_provider")}
            hitSlop={8}
            size={32}
            surface="soft"
            backgroundColor={semanticColors.surface.subtle}
            pressedBackgroundColor={semanticColors.overlay.pressed}
            pressedOpacity={1}
          >
            <ExternalLink
              size={16}
              color={EMBER.colors.textMid}
              strokeWidth={1.8}
            />
          </IconButton>
        </View>
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
        <ComparisonCell
          label={t("costs.financing_page.best_for")}
          value={provider.summary.value}
        />
        <ComparisonCell
          label={
            provider.productType === "fertility-insurance"
              ? t("costs.financing_page.coverage")
              : t("costs.financing_page.funding")
          }
          value={fundingValue}
        />
        <ComparisonCell
          label={t("costs.financing_page.pricing")}
          value={pricingValue}
        />
        <ComparisonCell
          label={t("costs.financing_page.credit_check")}
          value={creditValue}
        />
        <ComparisonCell
          label={t("costs.financing_page.provider_access")}
          value={accessValue}
        />
      </View>

      {provider.summary.caveat ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textLight,
          }}
        >
          {provider.summary.caveat}
        </Text>
      ) : null}
      {staleClaimCount > 0 ? (
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, lineHeight: 17, color: EMBER.colors.textMid }}
        >
          {t("costs.financing_page.review_pending_details")}
        </Text>
      ) : null}
    </CardSurface>
  );
}

export default function FinancingScreen({ route }: Props) {
  const t = useTranslations();
  const region = useRegion();
  const prefill = useApplicationPrefill();
  const [applicantSnapshot, setApplicantSnapshot] =
    useState<ApplicantSnapshot | null>(null);
  const [applicantDialogVisible, setApplicantDialogVisible] = useState(false);
  const [selectedProvider, setSelectedProvider] =
    useState<FinancingProvider | null>(null);
  const [heroCopied, setHeroCopied] = useState(false);
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });
  const hasApplicantInfo =
    applicantSnapshot !== null ||
    Boolean(
      prefill.fullName || prefill.dateOfBirth || prefill.email || prefill.phone,
    );

  const handleHeroCopy = async () => {
    if (!applicantSnapshot) return;
    const text = formatSnapshotForClipboard(applicantSnapshot);
    if (!text) return;
    try {
      await Clipboard.setStringAsync(text);
      setHeroCopied(true);
      haptic("success");
      setTimeout(() => setHeroCopied(false), 2400);
    } catch {
      // Clipboard access can fail silently (e.g. web permission denial).
    }
  };

  if (region === null) return null;
  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <BackLink
          label={route.params?.returnTab ? t("you.shared.back") : "Costs"}
          accessibilityLabel={
            route.params?.returnTab
              ? t("you.shared.back")
              : t("costs.back_to_costs")
          }
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        <ScreenHeader
          title={t("costs.financing")}
          subtitle={t("costs.financing_subtitle_default")}
        />

        <Hero
          eyebrow={{
            label: t("costs.financing_page.hero_eyebrow"),
            icon: WalletCards,
          }}
          title={t("costs.financing_page.hero_title")}
          summary={t("costs.financing_page.hero_summary")}
          primaryAction={{
            label: hasApplicantInfo
              ? t("costs.financing_page.edit_applicant_info")
              : t("costs.financing_page.prepare_applicant_info"),
            onPress: () => setApplicantDialogVisible(true),
            icon: Info,
          }}
          secondaryAction={
            applicantSnapshot
              ? {
                  label: heroCopied
                    ? t("costs.financing_page.applicant_info_copied_short")
                    : t("costs.financing_page.copy_applicant_info_short"),
                  onPress: handleHeroCopy,
                  icon: heroCopied ? Check : ClipboardCopy,
                }
              : undefined
          }
          animateBackground={Platform.OS === "web"}
          style={{ marginBottom: 16 }}
        />

        <View style={{ gap: 14 }}>
          {FINANCING_PROVIDERS.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              onOpenDetails={() => setSelectedProvider(provider)}
            />
          ))}
        </View>
      </ScrollView>

      <ApplicantInfoDialog
        visible={applicantDialogVisible}
        prefill={prefill}
        initialValue={applicantSnapshot}
        onClose={() => setApplicantDialogVisible(false)}
        onSave={(snapshot) => {
          setApplicantSnapshot(snapshot);
          setApplicantDialogVisible(false);
        }}
      />
      <FinancingFactSheet
        provider={selectedProvider}
        logo={
          selectedProvider
            ? (PROVIDER_LOGOS[selectedProvider.id] ?? null)
            : null
        }
        onClose={() => setSelectedProvider(null)}
      />
    </GlassScreenLayout>
  );
}
